import { useNodeId } from "@xyflow/react";
import { useCallback, useRef, Suspense } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  interfacesAtom_SUSPENDS,
  interfaceProjectAtomFamily_SUSPENDS,
  takeSnapshotSetter,
  formsTerminologyAtom,
} from "@zapier/canvas-state";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import { ErrorBoundary } from "../../ErrorBoundary";
import { NewAssetButtonWrapper } from "../NewAssetButtonWrapper";
import { CreateNewInterfaceButton } from "../../CreateNewInterfaceButton";
import { SelectedItem } from "../SelectedItem";

import styles from "./FloatingInterfacesTypeahead.module.css";

type Props = {
  interfaceProjectId: string | undefined;
};

type InnerProps = {
  interfaceProjectId: string | undefined;
};

function InterfacesTypeaheadLoader() {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label={`Search ${interfacesTerms.pluralLower}`}
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <TypeaheadItem key="loading" id="loading">
          Loading {interfacesTerms.pluralLower}...
        </TypeaheadItem>
      </Typeahead>
    </div>
  );
}

function SelectedInterfaceSuspended({ interfaceProjectId }: InnerProps) {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const nodeId = useNodeId()!;
  const selectedInterface = useAtomValue(
    interfaceProjectAtomFamily_SUSPENDS({
      interfaceProjectId: interfaceProjectId,
      nodeId,
    }),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        projectId: undefined,
        pageId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedInterface?.name || `${interfacesTerms.singular} not found`}
      icon={<Icon name="navInterfaces" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function InterfacesTypeaheadSuspended() {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const interfaces = useAtomValue(interfacesAtom_SUSPENDS);
  const { emitEventOnInterfaceConnect } = useEmitResourceConnectEvents();
  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          projectId: key,
          pageId: undefined, // need to reset the page if we change interface project
        },
      });

      if (key) {
        emitEventOnInterfaceConnect(String(key));
      }
    },
    [takeSnapshot, updateNodeAndSave, emitEventOnInterfaceConnect],
  );

  const PLACEHOLDER = `Search ${interfacesTerms.pluralLower}`;

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <NewAssetButtonWrapper>
        <CreateNewInterfaceButton />
      </NewAssetButtonWrapper>
      <Typeahead
        aria-label={PLACEHOLDER}
        placeholder={PLACEHOLDER}
        ref={inputRef}
        onChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {interfaces.map((interfaceProject) => {
          return (
            <TypeaheadItem
              key={interfaceProject.id}
              id={interfaceProject.id}
              textValue={interfaceProject.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navInterfaces" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{interfaceProject.name}</Text>
              </div>
            </TypeaheadItem>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function InterfacesTypeahead({ interfaceProjectId }: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<InterfacesTypeaheadLoader />}>
        {interfaceProjectId ? (
          <SelectedInterfaceSuspended interfaceProjectId={interfaceProjectId} />
        ) : (
          <InterfacesTypeaheadSuspended />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export function FloatingInterfacesTypeahead({ interfaceProjectId }: Props) {
  return <InterfacesTypeahead interfaceProjectId={interfaceProjectId} />;
}
